import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/ImageUploader";
import CategorySubcategoryPicker from "@/components/CategorySubcategoryPicker";
import { toast } from "sonner";

export default function AdminCreateOffer() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    business_id: sp.get("business") || "",
    title: "", description: "", category_id: "", subcategory_id: "", city: "",
    image: "", images: [], discount: "", advantage_type: "percentage", conditions: "", end_date: "",
    promo_code: "", max_redemptions: 0, coverage: "local",
    publish_now: true,
  });
  const [businesses, setBusinesses] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  useEffect(() => {
    api.get("/admin/businesses").then((r) => setBusinesses(r.data));
    api.get("/admin/cities").then((r) => setCities(r.data));
  }, []);

  const selectedBiz = businesses.find((b) => b.id === form.business_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_id) { toast.error("Choisissez un commerce"); return; }
    setLoading(true);
    try {
      await api.post("/admin/offers", form);
      toast.success(form.publish_now ? "Offre créée et publiée" : "Offre créée en attente");
      navigate("/admin/offres");
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold" data-testid="admin-create-offer-title">Créer une offre pour un professionnel</h1>
      <p className="text-sm text-slate-500 mt-1">En tant qu'admin vous pouvez publier directement, sans passer par la validation.</p>
      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl" data-testid="admin-create-offer-form">
        <div>
          <Label>Commerce</Label>
          <Select value={form.business_id} onValueChange={(v)=>{
            const b = businesses.find((x)=>x.id===v);
            setForm((s)=>({...s, business_id: v,
              category_id: s.category_id || b?.category_id || "",
              subcategory_id: s.subcategory_id || b?.subcategory_id || "",
              city: s.city || b?.city || "",
              coverage: b?.coverage || s.coverage,
            }));
          }}>
            <SelectTrigger data-testid="admin-biz-select"><SelectValue placeholder="Choisir un commerce"/></SelectTrigger>
            <SelectContent>{businesses.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} — {b.city}</SelectItem>))}</SelectContent>
          </Select>
          {selectedBiz && <div className="text-xs text-slate-500 mt-1">Propriétaire : {selectedBiz.owner?.first_name} {selectedBiz.owner?.last_name} · {selectedBiz.owner?.email}</div>}
        </div>

        <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-orange-50/40">
          <div>
            <div className="font-semibold text-sm">Publier immédiatement</div>
            <div className="text-xs text-slate-500">Sinon, l'offre passera en statut « En attente »</div>
          </div>
          <Switch checked={form.publish_now} onCheckedChange={(v)=>setForm((s)=>({...s, publish_now: v}))} data-testid="admin-publish-toggle"/>
        </div>

        <div>
          <Label>Zone de couverture</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "local"}))}
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="local"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Ville / pôle</div>
            </button>
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "department"}))}
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="department"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Tout l'Eure-et-Loir</div>
            </button>
          </div>
        </div>

        <div><Label>Titre</Label><Input required value={form.title} onChange={set("title")} data-testid="admin-offer-title-in"/></div>
        <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={set("description")}/></div>
        <CategorySubcategoryPicker
          value={{ category_id: form.category_id, subcategory_id: form.subcategory_id }}
          onChange={({ category_id, subcategory_id }) =>
            setForm((s) => ({ ...s, category_id, subcategory_id }))}
          testIdPrefix="admin-newoffer"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Ville</Label>
            <Select value={form.city} onValueChange={(v)=>setForm((s)=>({...s, city: v}))}>
              <SelectTrigger><SelectValue placeholder="Choisir"/></SelectTrigger>
              <SelectContent>{cities.map((c)=>(<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Réduction</Label><Input required value={form.discount} onChange={set("discount")}/></div>
          <div>
            <Label>Type d'avantage</Label>
            <Select value={form.advantage_type} onValueChange={(v)=>setForm((s)=>({...s, advantage_type: v}))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Pourcentage</SelectItem>
                <SelectItem value="fixed">Réduction fixe</SelectItem>
                <SelectItem value="formula">Formule</SelectItem>
                <SelectItem value="gift">Cadeau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Conditions</Label><Textarea rows={3} value={form.conditions} onChange={set("conditions")}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date de fin</Label>
            <Input type="date" data-testid="admin-offer-end-date" onChange={(e)=>setForm((s)=>({...s, end_date: e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : ""}))}/>
          </div>
          <div><Label>Code promo</Label><Input value={form.promo_code} onChange={set("promo_code")}/></div>
        </div>
        <div>
          <Label>Images de l'offre (jusqu'à 5)</Label>
          <div className="mt-2">
            <ImageUploader
              value={form.images}
              onChange={(imgs)=>setForm((s)=>({...s, images: imgs, image: imgs[0] || ""}))}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="btn-shine text-white" data-testid="admin-create-offer-submit">
            {loading ? "Enregistrement…" : (form.publish_now ? "Créer et publier" : "Créer en attente")}
          </Button>
          <Button type="button" variant="outline" onClick={()=>navigate("/admin/offres")}>Annuler</Button>
        </div>
      </form>
    </div>
  );
}
